<template lang="html">
  <div>
    <v-card flat>
      <v-card-title class="secondary white--text">
        <div class="ma-0">
          <div v-if="paymentGroups" class="d-flex align-center">
            <v-icon large left color="white">mdi-clipboard-check-multiple-outline</v-icon>
            <p class="subtitle-1 my-0">{{ paymentGroups.Count }} payment groups</p>
            <v-divider vertical class="mx-3 my-1" color="white"></v-divider>
            <span class="subtitle-1">{{ totalPayments }}</span>
          </div>
        </div>
        <v-spacer></v-spacer>
        <TableSearch v-model="search" />
      </v-card-title>

      <!-- Filters Row -->
      <v-card-text class="py-2 px-4 tertiary">
        <v-row align="center" no-gutters>
          <!-- State Filter -->
          <v-col cols="12" sm="6" md="3" class="pr-2">
            <v-select
              v-model="selectedState"
              :items="stateOptions"
              label="State"
              dense
              outlined
              hide-details
              clearable
              @change="handleFilterChange"
            ></v-select>
          </v-col>

          <!-- From Date -->
          <v-col cols="12" sm="6" md="3" class="px-2">
            <v-menu
              v-model="fromDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fromDate"
                  label="From Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  dense
                  outlined
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="handleFromDateClear"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fromDate"
                @input="fromDateMenu = false; handleFilterChange()"
                no-title
                scrollable
              ></v-date-picker>
            </v-menu>
          </v-col>

          <!-- To Date -->
          <v-col cols="12" sm="6" md="3" class="px-2">
            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="toDate"
                  label="To Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  dense
                  outlined
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="handleToDateClear"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="toDate"
                @input="toDateMenu = false; handleFilterChange()"
                no-title
                scrollable
              ></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Clear Filters Button -->
          <v-col cols="12" sm="6" md="3" class="pl-2 text-right">
            <v-btn
              v-if="hasActiveFilters"
              text
              small
              color="secondary"
              @click="clearAllFilters"
            >
              Clear Filters
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Table -->
      <v-data-table
        :headers="headers"
        :items="displayItems"
        :loading="isLoading"
        :search="search"
        :server-items-length="paymentGroups.Count || 0"
        :options.sync="tableOptions"
        @update:options="updateOptions"
        hide-default-footer
        class="elevation-0"
        no-data-text="No payments found"
      >
        <template v-slot:item.Amount="{ item }">
          <span>
            {{ item | returnDisplayAmount}}
          </span>
        </template>

        <template v-slot:item.CreatedAt="{ item }">
          <span>
            {{ item.CreatedAt | formatDate }}
          </span>
        </template>

        <template v-slot:item.financeEntries="{ item }">
          <v-btn outlined small color="#03A9F4" dark @click="openDialog(item)">
            <v-icon small>mdi-file-find</v-icon>
          </v-btn>
        </template>

        <template v-slot:item.support="{ item }">
          <v-btn @click="openMsgDialog(item)" text small outlined color="secondary">
            <v-icon small>mdi-comment-text-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>

        <!-- Custom Pagination Controls -->
        <v-card-actions class="pt-0">
          <v-spacer></v-spacer>
          <div class="d-flex align-center">
            <!-- Items per page selector -->
            <span class="grey--text caption mr-2">Items per page:</span>
            <v-select
              :value="localItemsPerPage"
              :items="[10, 25, 50]"
              @change="handleItemsPerPageChange"
              hide-details
              dense
              style="max-width: 60px"
              class="mr-4"
            ></v-select>

            <!-- Page info -->
            <span class="mx-2 caption">
              {{ pageStart }}-{{ pageEnd }} of {{ paymentGroups.Count || 0 }}
            </span>

            <!-- Page navigation buttons -->
            <v-btn
              icon
              small
              :disabled="localPage === 1 || isLoading"
              @click="handlePageChange(localPage - 1)"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              :disabled="localPage >= totalPages || isLoading"
              @click="handlePageChange(localPage + 1)"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-card-actions>
    </v-card>

    <!-- Full records dialog -->
    <v-dialog
      v-model="dialog"
      fullscreen 
      persistent
      max-width="500px"
      transition="dialog-transition"
    >
      <v-card>
        <v-card-title class="secondary white--text" primary-title>
          Finance Entries
          <v-spacer></v-spacer>
          <v-btn @click.stop="closeDialog()" outlined color="white">
            <v-icon>mdi-arrow-left</v-icon>Back
          </v-btn>
        </v-card-title>
        <v-card-text>
          <FinanceEntryTable 
            :records="this.selectedItem.FinanceEntries"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <MsgDialog
      :dialog="msgDialog"
      :details="selectedItem.Details"
      :source="source"
      :OID="selectedItem.PaymentGroupID"
      @closeDialog="closeMsgDialog"
      @msgSent="msgSent"
    />

    <Snackbar 
      :snackbarDialog="snackbar" 
      :snackbarText="snackbarText" 
      :snackbarColor="snackbarColor"
      @closeSnackbar="closeSnackbar()"
      :timeout="timeout"
    />
    
  </div>
</template>

<script>
import { mapState } from 'vuex'
import FinanceEntryTable from './ReconciledPaymentsFinanceEntriesTable'
import MsgDialog from '@/components/Dialogs/MessageDialog'
import Snackbar from '@/components/Snackbars/TheSnackbar'
import TableSearch from '@/components/TableComponents/TableSearch.vue'

export default {
  components: {
    FinanceEntryTable,
    MsgDialog,
    Snackbar,
    TableSearch
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      default: () => ({ page: 1, itemsPerPage: 10 })
    }
  },
  data() {
    return {
      source: 'Reconciled payments',
      snackbar: false,
      snackbarText: '',
      snackbarColor: '',
      timeout: 3000,
      dialog: false,
      msgDialog: false,
      loading: false,
      selectedItem: '',
      search: '',
      localPage: 1,
      localItemsPerPage: 10,
      isPending: false,
      tableOptions: {},
      headers: [
        { text: 'Amount', value: 'Amount', sortable: false },
        { text: 'Date', value: 'CreatedAt', sortable: false },
        { text: 'Details', value: 'Details', sortable: false },
        { text: 'State', value: 'State', sortable: false },
        { text: 'Reference', value: 'Reference', sortable: false },
        { text: 'Finance entries', value: 'financeEntries', sortable: false },
        { text: 'Support', value: 'support', sortable: false }
      ],
      // Filter data
      selectedState: null,
      fromDate: '',
      toDate: '',
      fromDateMenu: false,
      toDateMenu: false,
      stateOptions: [
        { text: 'Unassigned', value: 'Unassigned' },
        { text: 'Assigned', value: 'Assigned' },
        { text: 'Posted', value: 'Posted' }
      ]
    }
  },
  methods: {
    getColor(color) {
     return color.toLowerCase() + '--text'
    },
    openDialog(item) {
      this.selectedItem = item
      this.dialog = true
    },
    closeDialog() {
      this.dialog = false
      this.selectedItem = ''
    },
    openMsgDialog(item) {
      this.selectedItem = item
      this.msgDialog = true
    },
    closeMsgDialog() {
      this.msgDialog = false
      this.selectedItem = ''
    },
    msgSent(payload) {
      this.msgDialog = false
      this.snackbar = true,
      this.snackbarColor = payload.color
      this.snackbarText = payload.text
    },
    closeSnackbar() {
      this.snackbar = false
    },
    handlePageChange(newPage) {
      if (this.isPending || newPage === this.localPage) return
      
      this.isPending = true
      this.localPage = newPage
      this.emitPaginationChange()
      
      // Reset pending state after a short delay
      setTimeout(() => {
        this.isPending = false
      }, 500)
    },
    handleItemsPerPageChange(newItemsPerPage) {
      if (this.isPending || newItemsPerPage === this.localItemsPerPage) return
      
      this.isPending = true
      this.localItemsPerPage = newItemsPerPage
      this.localPage = 1 // Reset to first page when changing items per page
      this.emitPaginationChange()
      
      // Reset pending state after a short delay
      setTimeout(() => {
        this.isPending = false
      }, 500)
    },
    emitPaginationChange() {
      // Only emit if search is empty (server-side pagination)
      if (this.search === "") {
        this.$emit('paginate', this.localPage, this.localItemsPerPage, {
          state: this.selectedState,
          fromDate: this.fromDate,
          toDate: this.toDate
        });
      }
    },
    updateOptions() {
      // This method is called when table options change
      // We don't need to do anything here since we're using custom pagination
    },
    handleFilterChange() {
      // Reset to first page when filters change
      this.localPage = 1;
      this.$emit('filter-change', {
        state: this.selectedState,
        fromDate: this.fromDate,
        toDate: this.toDate
      });
    },
    handleFromDateClear() {
      this.fromDate = '';
      this.handleFilterChange();
    },
    handleToDateClear() {
      this.toDate = '';
      this.handleFilterChange();
    },
    clearAllFilters() {
      this.selectedState = null;
      this.fromDate = '';
      this.toDate = '';
      this.handleFilterChange();
    }
  },
  computed: {
    ...mapState({
      paymentGroups: state => state.UnreconciledPayments.paymentGroupsRecords
    }),
    hasActiveFilters() {
      return this.selectedState !== null || this.fromDate || this.toDate;
    },
    totalPayments() {
      const array = this.paymentGroups.Data
      const payments = array.map(val => {
        return val.Amount
      })
      const total = payments.reduce(
        (acc, val) => acc + val,
        0
      )
      // return total.toLocaleString()
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(total)
    },
    displayItems() {
      if (!this.paymentGroups || !this.paymentGroups.Data) return []
      
      // For server-side pagination, we show all data received
      // Search is handled client-side on the current page only
      if (!this.search) {
        return this.paymentGroups.Data
      }
      
      const searchLower = this.search.toLowerCase()
      return this.paymentGroups.Data.filter(item => {
        return Object.values(item).some(value => 
          String(value).toLowerCase().includes(searchLower)
        )
      })
    },
    totalPages() {
      if (!this.paymentGroups || !this.paymentGroups.Count) return 1
      return Math.ceil(this.paymentGroups.Count / this.localItemsPerPage)
    },
    pageStart() {
      if (!this.paymentGroups || !this.paymentGroups.Count || this.paymentGroups.Count === 0) return 0
      return ((this.localPage - 1) * this.localItemsPerPage) + 1
    },
    pageEnd() {
      if (!this.paymentGroups || !this.paymentGroups.Count) return 0
      const end = this.localPage * this.localItemsPerPage
      return end > this.paymentGroups.Count ? this.paymentGroups.Count : end
    }
  },
  watch: {
    pagination: {
      immediate: true,
      handler(val) {
        if (val) {
          this.localPage = val.page || 1
          this.localItemsPerPage = val.itemsPerPage || 10
        }
      }
    }
  },
  filters: {
    returnDisplayAmount(val) {
      return val.DisplayAmount
    }
  },

}
</script>

<style lang="css" scoped>
.position-relative {
  position: relative;
}
</style>
